export const M10_UNIT_9 = {
  id: "m10-u9-say-tell",
  title: "Unit 9: Saying & Telling",
  topic: "Say vs Tell + Collocations + Reported Speech",
  iconType: "mic",
  grammarImage: "/m10-u9-say-tell.jpg",
  grammarText: "Unit focus: The difference between 'say' and 'tell' — core rules, fixed collocations, and how to use both correctly in reported speech and everyday storytelling.",

  sets: [

    // ===================================================================
    // SET 1: SAY SOMETHING · SAY ANYTHING · SAY YES / NO (B1+)
    // ===================================================================
    {
      id: "m10-u9-set1",
      title: "Set 1: Finding the Words",
      context: `*Toronto, Monday morning — Mia's first live broadcast*

Mia is 31 and has just been given her own radio show.
She had dreamed of this for years. Now, sitting in front of the microphone,
she couldn't say anything. Her producer Yemi counted her in: 3... 2... 1...
"Good morning, Toronto. I have something to say."

*Toronto, lunes por la mañana — Primera emisión en directo de Mia*

Mia tiene 31 años y acaba de conseguir su propio programa de radio.
Había soñado con esto durante años. Ahora, sentada frente al micrófono,
no podía decir nada. Su productora Yemi le dio la entrada: 3... 2... 1...
"Buenos días, Toronto. Tengo algo que decir."`,

      questions: [
        {
          type: "match",
          q: "Match the SAY collocations:",
          pairs: [
            { left: "say something", right: "decir algo" },
            { left: "say anything", right: "decir algo/nada (negativo o interrogativo)" },
            { left: "say yes / say no", right: "decir que sí / decir que no" }
          ],
          a: "completed",
          successTip: "SAY doesn't need a person object! 'She said something.' ✅ 'She said me something.' ❌ This is the #1 mistake learners make with say! 🚫"
        },

        {
          type: "mc",
          q: "Before the broadcast, Mia couldn't ___ anything — she was completely frozen.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say anything = decir nada/algo. No person object needed."
        },

        {
          type: "mc",
          q: "When the network offered her the show, she ___ yes immediately.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say yes = aceptar. Fixed collocation — always SAY, never tell!",
          successTip: "Say yes / say no = always SAY! Never 'tell yes'. 'Did she say yes?' = ¿Dijo que sí? Used for any agreement or refusal! 🎙️"
        },

        {
          type: "match",
          q: "Match the core SAY vs TELL rule:",
          pairs: [
            { left: "SAY", right: "No necesita objeto de persona: 'She said something.'" },
            { left: "TELL", right: "Necesita objeto de persona: 'She told ME something.'" },
            { left: "EXCEPCIÓN TELL", right: "tell the truth, tell a story, tell lies (sin persona)" }
          ],
          a: "completed",
          successTip: "The golden rule! SAY = no person needed. TELL = person needed. EXCEPT for fixed tell collocations (truth, lies, story, difference). Remember the exceptions! 🔑"
        },

        {
          type: "mc",
          q: "Mia finally ___ something — her voice was shaking but clear.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say something = decir algo. No object needed."
        },

        {
          type: "mc",
          q: "The network director ___ no to her first idea for the show.",
          options: ["said", "told", "replied"],
          a: "said",
          tip: "Say no = decir que no. Fixed collocation — always SAY.",
          successTip: "Say no = rechazar. 'She said no to the first offer but yes to the second.' Powerful in negotiation contexts! Very common in professional English! 💼"
        },

        {
          type: "mc",
          q: "Yemi whispered: 'Don't worry — just ___ what you feel.'",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say what you feel = express yourself. No person object = say."
        },

        {
          type: "order",
          q: "Traduce: 'Ella quería decir algo, pero no podía decir nada.'",
          words: ["She", "wanted", "to", "say", "something,", "but", "she", "couldn't", "say", "anything."],
          a: "She wanted to say something, but she couldn't say anything.",
          extra: ["tell", "speak", "nothing"]
        },

        {
          type: "mc",
          q: "The audience ___ nothing for a moment — then the applause began.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say nothing = no decir nada. Always SAY with these expressions.",
          successTip: "'Say nothing' = quedarse callado. 'He said nothing and walked away.' Very dramatic in storytelling! Also: 'say nothing more' = no añadir nada más! 🤐"
        },

        {
          type: "input",
          q: "Mia's diary: 'I finally ___ yes to my dream — and I couldn't ___ anything for the first five seconds.' (say/say)",
          a: "said / say",
          alts: ["Said / say", "said / Say"],
          successTip: "Say yes (commitment) + say anything (speech act) — both SAY, no person needed. The show has started! 🎙️✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: TELL THE TRUTH · TELL LIES · TELL A WHITE LIE (B1+)
    // ===================================================================
    {
      id: "m10-u9-set2",
      title: "Set 2: Truth, Lies & White Lies",
      context: `*Toronto, Wednesday — Mia interviews Daniel live on air*

Daniel is 38, an Irish comedian known for blurring the line between truth and fiction.
Mia had been warned: "You'll never know when he's telling the truth."
Daniel arrived late, winked at the microphone, and said:
"I always tell the truth. Occasionally I also tell lies. But never at the same time."

*Toronto, miércoles — Mia entrevista a Daniel en directo*

Daniel tiene 38 años, un comediante irlandés conocido por difuminar la línea entre la verdad y la ficción.
A Mia le habían advertido: "Nunca sabrás cuándo dice la verdad."
Daniel llegó tarde, guiñó un ojo al micrófono y dijo:
"Siempre digo la verdad. Ocasionalmente también miento. Pero nunca al mismo tiempo."`,

      questions: [
        {
          type: "match",
          q: "Match the TELL collocations:",
          pairs: [
            { left: "tell the truth", right: "decir la verdad" },
            { left: "tell lies", right: "decir mentiras / mentir" },
            { left: "tell a white lie", right: "decir una mentira piadosa" }
          ],
          a: "completed",
          successTip: "These TELL collocations are special — they DON'T need a person object! 'She told the truth.' ✅ NOT 'she told me the truth' (both ok but person not required here)! 🤍"
        },

        {
          type: "mc",
          q: "Daniel always claimed he was ___ the truth, even when nobody believed him.",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Tell the truth = fixed collocation. Always TELL, never say the truth!",
          successTip: "'Tell the truth' — never 'say the truth'! This is one of the most common errors in English. Truth collocates with TELL. Always! ✅"
        },

        {
          type: "mc",
          q: "Mia suspected he had been ___ lies for the past ten minutes.",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Tell lies = mentir. Fixed collocation — always TELL.",
          successTip: "Tell lies = decir mentiras. 'He was telling lies the whole time!' You can also say 'lying' — but the collocation is specifically TELL lies! 🤥"
        },

        {
          type: "mc",
          q: "Daniel said: 'A comedian who never ___ a white lie is not a good storyteller.'",
          options: ["tells", "says", "speaks"],
          a: "tells",
          tip: "Tell a white lie = decir una mentira piadosa. Always TELL.",
          successTip: "A white lie = una mentira piadosa (harmless, told to protect feelings). 'I told a white lie — I said her cooking was great.' TELL, never SAY a white lie! 🤍"
        },

        {
          type: "mc",
          q: "Mia asked Daniel directly: 'Were you ___ the truth just now?'",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Tell the truth — always TELL in questions and statements."
        },

        {
          type: "mc",
          q: "Daniel laughed: 'I never ___ lies — I just ___ stories with flexible endings.'",
          options: ["tell / tell", "say / say", "tell / say"],
          a: "tell / tell",
          tip: "Tell lies + tell stories = both fixed TELL collocations.",
          successTip: "Two TELL collocations in one sentence! Tell lies + tell stories — Daniel's whole character summed up in one line! Collocations reveal personality! 🎭"
        },

        {
          type: "mc",
          q: "Yemi had told Mia before the show that Daniel sometimes ___ things up entirely.",
          options: ["made", "said", "told"],
          a: "made",
          tip: "Make things up = inventarse cosas. Different from tell lies — less deliberate!",
          successTip: "'Make things up' = inventarse cosas. Related but different from 'tell lies'! Lies = deliberate. Making things up = could be imaginative or dishonest! 🎨"
        },

        {
          type: "order",
          q: "Traduce: 'Siempre dice la verdad, pero a veces también dice mentiras piadosas.'",
          words: ["He", "always", "tells", "the", "truth,", "but", "sometimes", "he", "also", "tells", "white", "lies."],
          a: "He always tells the truth, but sometimes he also tells white lies.",
          extra: ["says", "speaks", "lying"]
        },

        {
          type: "mc",
          q: "Mia said to the audience: 'I honestly can't tell if he's ___ the truth or ___ lies.'",
          options: ["telling / telling", "saying / saying", "telling / saying"],
          a: "telling / telling",
          tip: "Both are TELL collocations — tell the truth AND tell lies."
        },

        {
          type: "input",
          q: "Daniel's quote: 'The best comedians always ___ the truth — they just wrap it in a good story.' (decir la verdad)",
          a: "tell",
          alts: ["Tell"],
          successTip: "Tell the truth, tell lies, tell a white lie — all TELL! Three honest mistakes eliminated forever! 🏆🤍"
        }
      ]
    },

    // ===================================================================
    // SET 3: SAY HELLO/GOODBYE · SAY SO · TELL SOMEONE (B1+)
    // ===================================================================
    {
      id: "m10-u9-set3",
      title: "Set 3: Hellos, Goodbyes & Telling People",
      context: `*Toronto, Thursday — behind the scenes at the radio station*

After Daniel's chaotic interview, Yemi had a lot to deal with.
She had to tell the network what had happened.
Several colleagues stopped by just to say hello — and to find out the gossip.
One of them left without saying goodbye, which Yemi said was typical.

*Toronto, jueves — entre bastidores en la emisora de radio*

Después de la caótica entrevista de Daniel, Yemi tenía mucho que gestionar.
Tuvo que decirle a la cadena lo que había pasado.
Varios colegas pasaron a saludar — y a enterarse del cotilleo.
Uno de ellos se fue sin despedirse, lo que Yemi dijo que era típico.`,

      questions: [
        {
          type: "match",
          q: "Match the SAY and TELL collocations:",
          pairs: [
            { left: "say hello / goodbye", right: "saludar / despedirse" },
            { left: "say so", right: "decir eso / afirmarlo" },
            { left: "tell someone", right: "decirle a alguien / contar a alguien" }
          ],
          a: "completed",
          successTip: "'Say hello' and 'say goodbye' = always SAY! Never 'tell hello'. 'She said goodbye' ✅ 'She told goodbye' ❌ Greetings and farewells = always SAY! 👋"
        },

        {
          type: "mc",
          q: "Several colleagues stopped by to ___ hello and hear what had happened.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say hello = saludar. Fixed collocation — always SAY.",
          successTip: "Say hello, say goodbye, say goodnight, say good morning — ALL with SAY! Greetings are always said, never told. 'She said hello to everyone.' ✅ 👋"
        },

        {
          type: "mc",
          q: "One colleague left without ___ goodbye — very rude, Yemi thought.",
          options: ["saying", "telling", "speaking"],
          a: "saying",
          tip: "Say goodbye = despedirse. Always SAY — and here: without saying."
        },

        {
          type: "mc",
          q: "Yemi needed to ___ the network director exactly what had gone wrong.",
          options: ["tell", "say", "speak"],
          a: "tell",
          tip: "Tell someone = contar/decir a alguien. TELL needs a person object!",
          successTip: "Tell someone = TELL + person object. 'I need to tell you something important.' The person (you, them, him, her) is essential with TELL! 👤"
        },

        {
          type: "mc",
          q: "Mia had warned Yemi about Daniel. Yemi should have ___ so to the network earlier.",
          options: ["said", "told", "spoken"],
          a: "said",
          tip: "Say so = decir eso / afirmarlo. Fixed expression — always SAY so.",
          successTip: "'Say so' = decirlo / afirmarlo. 'If you disagree, say so!' = ¡Si no estás de acuerdo, dilo! Very direct and common in professional English! 💼"
        },

        {
          type: "mc",
          q: "Yemi ___ the director that Daniel had gone off-script during the interview.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person (the director) + that clause = TELL with person object."
        },

        {
          type: "mc",
          q: "The director ___ that it had actually been great radio.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person object) = SAY.",
          successTip: "Compare! 'The director SAID that it was great.' (no person) vs 'The director TOLD Yemi that it was great.' (with person). Same information, different structure! 🔄"
        },

        {
          type: "order",
          q: "Traduce: 'Ella le dijo a su jefe lo que había pasado, y él dijo que no era problema.'",
          words: ["She", "told", "her", "boss", "what", "had", "happened,", "and", "he", "said", "it", "was", "not", "a", "problem."],
          a: "She told her boss what had happened, and he said it was not a problem.",
          extra: ["said", "told", "spoke"]
        },

        {
          type: "mc",
          q: "Mia ___ Yemi that she was sorry for the chaos — and Yemi ___ it was fine.",
          options: ["told / said", "said / told", "told / told"],
          a: "told / said",
          tip: "Told Yemi (person object) + said it was fine (no person object).",
          successTip: "One sentence, both verbs! 'She TOLD Yemi...' (person needed) + 'Yemi SAID it was fine' (no person). Perfect contrast in one sentence! 🎯"
        },

        {
          type: "input",
          q: "Yemi's rule: 'If something bothers you, ___ so. Don't stay silent.' (afírmalo / dilo)",
          a: "say",
          alts: ["Say"],
          successTip: "Say so = dilo / afírmalo. One of the most useful fixed expressions in English! 'If you want help, say so!' Simple, direct, powerful! 💬✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: TELL A STORY · TELL THE DIFFERENCE (B2-)
    // ===================================================================
    {
      id: "m10-u9-set4",
      title: "Set 4: Stories & Differences",
      context: `*Dublin, Saturday — Daniel at a storytelling festival*

Daniel had been invited to perform at Ireland's biggest storytelling festival.
He had always been able to tell a story better than almost anyone alive.
But the host asked him a difficult question during the Q&A:
"Can you tell the difference between your real life and the stories you tell?"

*Dublín, sábado — Daniel en un festival de narración*

Daniel había sido invitado a actuar en el festival de narración más grande de Irlanda.
Siempre había sido capaz de contar una historia mejor que casi cualquiera.
Pero el presentador le hizo una pregunta difícil durante el turno de preguntas:
"¿Puedes distinguir entre tu vida real y las historias que cuentas?"`,

      questions: [
        {
          type: "match",
          q: "Match the TELL collocations:",
          pairs: [
            { left: "tell a story", right: "contar una historia" },
            { left: "tell the difference", right: "distinguir / notar la diferencia" },
            { left: "tell someone", right: "decirle a alguien / contar a alguien" }
          ],
          a: "completed",
          successTip: "'Tell the difference' = distinguir. 'Can you tell the difference between butter and margarine?' TELL = used for identification and distinction! 🔍"
        },

        {
          type: "mc",
          q: "Daniel could ___ a story that made an entire room fall silent.",
          options: ["tell", "say", "speak"],
          a: "tell",
          tip: "Tell a story = contar una historia. Always TELL — never say a story!",
          successTip: "'Tell a story' — NEVER 'say a story'! ❌ Also: tell a joke = contar un chiste. Tell a tale = contar un cuento. All storytelling = TELL! 📖"
        },

        {
          type: "mc",
          q: "The host asked: 'Can you ___ the difference between your real life and your fiction?'",
          options: ["tell", "say", "know"],
          a: "tell",
          tip: "Tell the difference = distinguir/notar la diferencia.",
          successTip: "'Tell the difference' — never 'say the difference'! ❌ 'I can't tell the difference between the twins!' = No puedo distinguir entre los gemelos. Very common! 👯"
        },

        {
          type: "mc",
          q: "Daniel laughed: 'Honestly? I can't always ___ the difference myself.'",
          options: ["tell", "say", "find"],
          a: "tell",
          tip: "Tell the difference = identify/distinguish. Fixed TELL collocation.",
          successTip: "Extended uses: 'Tell the difference between A and B', 'Can you tell them apart?', 'They look identical — I can't tell!' All use TELL for identification! 🔎"
        },

        {
          type: "mc",
          q: "He ___ a story about his grandmother that made everyone in the room cry.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told a story = Past Simple of tell a story."
        },

        {
          type: "mc",
          q: "Daniel had been ___ stories since he was five years old.",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Telling stories = continuous form of tell a story.",
          successTip: "Tell stories, tell jokes, tell tales — Daniel's whole skill set uses TELL! 'He's been telling stories his whole life.' Natural and fluent! 🎭"
        },

        {
          type: "mc",
          q: "The audience couldn't ___ the difference between his real memories and invented ones.",
          options: ["tell", "say", "find"],
          a: "tell",
          tip: "Tell the difference = distinguish between two things."
        },

        {
          type: "order",
          q: "Traduce: '¿Puedes distinguir entre una mentira piadosa y la verdad cuando él habla?'",
          words: ["Can", "you", "tell", "the", "difference", "between", "a", "white", "lie", "and", "the", "truth", "when", "he", "speaks?"],
          a: "Can you tell the difference between a white lie and the truth when he speaks?",
          extra: ["say", "know", "find"]
        },

        {
          type: "mc",
          q: "After the show, Daniel ___ the audience that everything he had said was true.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person (the audience) + that clause = TELL.",
          successTip: "Told the audience = TELL + person object. Even with a large group ('the audience', 'everyone', 'them'), it's still TELL + person! 👥"
        },

        {
          type: "input",
          q: "Festival review: 'Daniel ___ a story so vivid that nobody could ___ the difference between memory and imagination.' (tell / tell)",
          a: "told / tell",
          alts: ["Told / tell"],
          successTip: "Tell a story + tell the difference — both TELL, no SAY! Two powerful collocations, one Irish comedian, one unforgettable night! 🍀📖"
        }
      ]
    },

    // ===================================================================
    // SET 5: SAY vs TELL — CORE CONTRAST (B2-)
    // ===================================================================
    {
      id: "m10-u9-set5",
      title: "Set 5: Say or Tell? The Full Picture",
      context: `*Toronto, Sunday — dinner party at Mia's apartment*

Mia had invited Daniel, Yemi, and Priya for dinner.
Everyone had something to say. Everyone had something to tell.
"That's the funny thing about language," said Daniel.
"Sometimes you say things. Sometimes you tell people things. But always — you mean something."

*Toronto, domingo — cena en el apartamento de Mia*

Mia había invitado a Daniel, Yemi y Priya a cenar.
Todos tenían algo que decir. Todos tenían algo que contar.
"Eso es lo gracioso del lenguaje," dijo Daniel.
"A veces dices cosas. A veces le cuentas cosas a la gente. Pero siempre — quieres decir algo."`,

      questions: [
        {
          type: "match",
          q: "Match the correct verb for each collocation:",
          pairs: [
            { left: "the truth / a story / lies", right: "TELL (collocations fijas sin persona)" },
            { left: "something / anything / yes / no / hello", right: "SAY (sin objeto de persona)" },
            { left: "someone / me / them / the director", right: "TELL (siempre con persona)" }
          ],
          a: "completed",
          successTip: "The complete map! SAY = no person. TELL = person OR fixed collocation. When in doubt: does it need a person? → TELL. Does it not? → SAY (or check fixed collocations)! 🗺️"
        },

        {
          type: "mc",
          q: "Priya had a lot to ___ about her recent travels — she talked for twenty minutes.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Have a lot to say = tener mucho que decir. No person object = SAY."
        },

        {
          type: "mc",
          q: "Daniel ___ everyone at the table a hilarious story about his flight.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told everyone (person) + a story = TELL with both person and collocation!",
          successTip: "'Told everyone a story' = TELL + person (everyone) + collocation (a story). When you have BOTH a person AND a collocation, TELL is definitely correct! ✅"
        },

        {
          type: "mc",
          q: "Mia asked Yemi: 'Did he ___ anything to you about the festival before tonight?'",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say anything TO someone = SAY (not tell) when followed by 'to + person'.",
          successTip: "'Say something TO someone' = SAY + to + person. 'Did he say anything TO you?' ✅ Different from 'tell you' — say keeps its no-object rule but adds 'to'! 💡"
        },

        {
          type: "mc",
          q: "Yemi: 'He ___ me he was going to be late, but he didn't ___ why.'",
          options: ["told / say", "said / tell", "told / tell"],
          a: "told / say",
          tip: "Told me (person object) = TELL. Didn't say why (no person) = SAY.",
          successTip: "Perfect contrast in one sentence! TOLD me (person present) + SAY why (no person). This is the pattern to master! 🎯"
        },

        {
          type: "mc",
          q: "Priya ___ that Daniel was the most unpredictable person she had ever interviewed.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person object between said and that) = SAY."
        },

        {
          type: "mc",
          q: "Daniel insisted he always ___ the truth, even when it sounded like fiction.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Tell the truth = fixed TELL collocation. Never say the truth!"
        },

        {
          type: "mc",
          q: "After dinner, Mia ___ goodnight to everyone and ___ them she'd had a wonderful evening.",
          options: ["said / told", "told / said", "said / said"],
          a: "said / told",
          tip: "Say goodnight (greeting/farewell) + told them (person object). Both patterns in one!",
          successTip: "Say goodnight + told them — greetings are SAY but reporting to a person is TELL. One dinner, two structures mastered! 🌙✅"
        },

        {
          type: "order",
          q: "Traduce: 'Él le dijo la verdad, pero ella dijo que no le creía.'",
          words: ["He", "told", "her", "the", "truth,", "but", "she", "said", "she", "didn't", "believe", "him."],
          a: "He told her the truth, but she said she didn't believe him.",
          extra: ["said", "told", "told"]
        },

        {
          type: "input",
          q: "Daniel's toast: 'Here's to people who ___ stories, ___ the truth, and always ___ something worth hearing!' (tell / tell / say)",
          a: "tell / tell / say",
          alts: ["Tell / tell / say"],
          successTip: "Tell stories + tell the truth (TELL collocations) + say something (SAY with no person). Three collocations, one perfect toast! 🥂🎉"
        }
      ]
    },

    // ===================================================================
    // SET 6: SAY/TELL IN REPORTED SPEECH (B2-)
    // ===================================================================
    {
      id: "m10-u9-set6",
      title: "Set 6: In Her Own Words",
      context: `*Mumbai — Priya writes up her interview with Mia*

Priya had taken pages of notes during the dinner.
Now she was turning them into an article.
She had to decide carefully: when to write 'Mia said' and when to write 'Mia told me'.
"Getting say and tell right in reported speech," she wrote, "is half the battle."

*Bombay — Priya escribe su entrevista con Mia*

Priya había tomado páginas de notas durante la cena.
Ahora las convertía en un artículo.
Tenía que decidir con cuidado: cuándo escribir 'Mia dijo' y cuándo 'Mia me dijo'.
"Usar bien say y tell en estilo indirecto," escribió, "es la mitad de la batalla."`,

      questions: [
        {
          type: "match",
          q: "Match the reported speech patterns:",
          pairs: [
            { left: "She said (that) she was nervous.", right: "SAY + that clause (sin persona entre said y that)" },
            { left: "She told me (that) she was nervous.", right: "TELL + persona + that clause" },
            { left: "She said to me that she was nervous.", right: "SAY + to + persona + that clause" }
          ],
          a: "completed",
          successTip: "Three ways to report! Said that (neutral), told me that (direct to person), said TO me that (said + to + person). All correct — but told me is most natural in speech! 💬"
        },

        {
          type: "mc",
          q: "Mia ___ she had always wanted her own show.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person between said and the clause) = SAY."
        },

        {
          type: "mc",
          q: "Mia ___ Priya she had always wanted her own show.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person (Priya) + that clause = TELL.",
          successTip: "Compare! 'Mia SAID she wanted...' vs 'Mia TOLD Priya she wanted...' Same information — the PERSON makes the difference! Person present = TELL! 👤"
        },

        {
          type: "mc",
          q: "Daniel ___ that the festival had been the best night of his career.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person directly after said) = SAY."
        },

        {
          type: "mc",
          q: "Daniel ___ everyone that the festival had been the best night of his career.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + everyone (person) + that clause = TELL.",
          successTip: "Again — EVERYONE = a person object! Even groups work as person objects for TELL. 'He told everyone, he told them, he told the world.' All TELL! 🌍"
        },

        {
          type: "mc",
          q: "Yemi ___ to Priya that she had been managing radio talent for twenty years.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said TO + person = SAY (not TELL). Said to = SAY with 'to' added.",
          successTip: "'Said to me' vs 'told me' — both natural! But 'said to' is more formal/literary. 'He said to her that he loved her.' vs 'He told her he loved her.' Same meaning! ❤️"
        },

        {
          type: "mc",
          q: "Priya wrote: 'Mia ___ the truth about how hard the first year had been.'",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Tell the truth = fixed TELL collocation (no person object needed here)."
        },

        {
          type: "order",
          q: "Traduce: 'Ella me dijo que había estado trabajando en la radio durante cinco años.'",
          words: ["She", "told", "me", "she", "had", "been", "working", "in", "radio", "for", "five", "years."],
          a: "She told me she had been working in radio for five years.",
          extra: ["said", "spoke", "for"]
        },

        {
          type: "mc",
          q: "Priya's article: 'Mia ___ that hosting a live show was both terrifying and exhilarating.'",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person between said and clause) = SAY."
        },

        {
          type: "input",
          q: "Priya's final line: 'She ___ me something I won't forget: the truth is always better radio than fiction.' (contar a alguien)",
          a: "told",
          alts: ["Told"],
          successTip: "Told me something = TELL + person + object. Perfect close! Priya got both verbs right in every sentence. That's the article written! 📰✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: SAY SORRY · SAY NOTHING · TELL TALES (B2)
    // ===================================================================
    {
      id: "m10-u9-set7",
      title: "Set 7: Apologies, Silences & Gossip",
      context: `*Toronto, the following Monday — aftermath of the interview*

One listener had complained about Daniel's interview.
Mia knew she had to say sorry. Daniel knew too — but said nothing.
Yemi had warned them both that someone on the team had been telling tales.
"This business," Yemi said quietly, "runs on secrets and say-so."

*Toronto, el lunes siguiente — las secuelas de la entrevista*

Un oyente se había quejado de la entrevista de Daniel.
Mia sabía que tenía que disculparse. Daniel también lo sabía — pero no dijo nada.
Yemi les había advertido a ambos que alguien del equipo había estado cotilleando.
"Este negocio," dijo Yemi tranquilamente, "funciona con secretos y rumores."`,

      questions: [
        {
          type: "match",
          q: "Match the extended SAY/TELL collocations:",
          pairs: [
            { left: "say sorry", right: "disculparse / pedir perdón" },
            { left: "say nothing", right: "no decir nada / guardar silencio" },
            { left: "tell tales", right: "cotillear / chivarse / contar chismes" }
          ],
          a: "completed",
          successTip: "'Tell tales' = British English for gossip/snitching. 'Don't tell tales!' = ¡No te chives! Also: 'tell tales out of school' = revelar secretos internos! 🕵️"
        },

        {
          type: "mc",
          q: "Mia knew she had to ___ sorry to the listener who had complained.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say sorry = disculparse. Fixed collocation — always SAY.",
          successTip: "'Say sorry' = disculparse informally. More formal: 'apologise'. 'I said sorry but she didn't accept it.' Say sorry is warmer and more personal! 💙"
        },

        {
          type: "mc",
          q: "Daniel ___ nothing for a long moment — then finally nodded.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say nothing = guardar silencio. Always SAY — even in the negative.",
          successTip: "'Said nothing' = guardó silencio. Very powerful in storytelling! 'He said nothing and walked away.' Silence can say everything — but grammatically it's SAY! 🤐"
        },

        {
          type: "mc",
          q: "Someone on the team had been ___ tales about what happened in the studio.",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Tell tales = cotillear / chivarse. Fixed TELL collocation.",
          successTip: "Tell tales = contar chismes/secretos de otros. 'Who's been telling tales?' = ¿Quién se ha ido de la lengua? Very British expression! 🇬🇧👀"
        },

        {
          type: "mc",
          q: "Yemi ___ both Mia and Daniel that the leak had to stop immediately.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + both Mia and Daniel (people) + that clause = TELL."
        },

        {
          type: "mc",
          q: "Daniel finally ___ sorry — it had taken him three days.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say sorry = fixed SAY collocation. Said sorry = Past Simple.",
          successTip: "Saying sorry takes courage! But grammatically it's simple: always SAY sorry, never TELL sorry. ❌ 'He said sorry and meant it.' 💙"
        },

        {
          type: "mc",
          q: "Mia ___ nothing when Daniel apologised — her silence said more than words.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say nothing = guardar silencio (SAY in all forms)."
        },

        {
          type: "order",
          q: "Traduce: 'Alguien había estado cotilleando, pero nadie quería decir nada.'",
          words: ["Someone", "had", "been", "telling", "tales,", "but", "nobody", "wanted", "to", "say", "anything."],
          a: "Someone had been telling tales, but nobody wanted to say anything.",
          extra: ["saying", "telling", "nothing"]
        },

        {
          type: "mc",
          q: "Yemi's rule: 'In this industry, ___ nothing you wouldn't want repeated on air.'",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say nothing = don't say anything (advice form).",
          successTip: "Say nothing you wouldn't want repeated = advice using SAY. 'Say nothing and nobody can misquote you.' Wise words for any industry! 🎙️"
        },

        {
          type: "input",
          q: "Crisis resolved: 'Mia ___ sorry, Daniel ___ nothing more about it, and whoever had been ___ tales was never identified.' (say / say / tell)",
          a: "said / said / telling",
          alts: ["Said / said / telling"],
          successTip: "Three collocations in one sentence! Said sorry + said nothing + telling tales. Real English, real rhythm, real grammar! 🎯🎙️"
        }
      ]
    },

    // ===================================================================
    // SET 8: TELL SOMEONE OFF · SAY YOUR PIECE (B2)
    // ===================================================================
    {
      id: "m10-u9-set8",
      title: "Set 8: Speaking Up & Standing Firm",
      context: `*Toronto, Wednesday — a crisis at the radio station*

The network director had called an emergency meeting.
An intern had leaked details of a future broadcast to a rival station.
Yemi had already told the intern off — privately, before the meeting.
But Mia still had things to say. She needed to say her piece.

*Toronto, miércoles — una crisis en la emisora de radio*

El director de la cadena había convocado una reunión de emergencia.
Un becario había filtrado detalles de una futura emisión a una emisora rival.
Yemi ya había reprendido al becario — en privado, antes de la reunión.
Pero Mia todavía tenía cosas que decir. Necesitaba decir lo que pensaba.`,

      questions: [
        {
          type: "match",
          q: "Match the idiomatic SAY/TELL expressions:",
          pairs: [
            { left: "tell someone off", right: "regañar / reprender a alguien" },
            { left: "say your piece", right: "decir lo que uno piensa / expresarse" },
            { left: "say the word", right: "dar la señal / decir que sí y se hace" }
          ],
          a: "completed",
          successTip: "'Tell someone off' = to scold/reprimand. 'She told me off for being late.' TELL + person + off. The person is always between TELL and OFF! 🔴"
        },

        {
          type: "mc",
          q: "Yemi had already ___ the intern ___ before the meeting even started.",
          options: ["told / off", "said / off", "told / down"],
          a: "told / off",
          tip: "Tell someone off = regañar a alguien. TELL + person + OFF.",
          successTip: "You used it! ✅ Tell + person + off. 'She told him off.' / 'She told them off.' The person MUST go between TELL and OFF! Never 'told off him'! ❌"
        },

        {
          type: "mc",
          q: "Mia needed to ___ her piece — she had been quiet for too long.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say your piece = express what you think. Fixed SAY expression.",
          successTip: "Say your piece = decir lo que piensas de una vez. 'Let her say her piece before you respond.' Very useful in meetings and arguments! 💼🗣️"
        },

        {
          type: "mc",
          q: "At the meeting, Mia ___ clearly that the leak had damaged trust across the whole team.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person directly after said) = SAY."
        },

        {
          type: "mc",
          q: "She also ___ the director that better protocols were needed immediately.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person (the director) + that clause = TELL.",
          successTip: "Back-to-back: 'She SAID clearly that...' (no person) then 'She TOLD the director that...' (with person). Spotting the pattern in natural text = real fluency! ✅"
        },

        {
          type: "mc",
          q: "The director told Mia: 'Just ___ the word and we'll upgrade all security systems.'",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say the word = dar la señal. Fixed SAY expression.",
          successTip: "'Just say the word' = solo di que sí / solo dímelo. 'If you need help, say the word.' Very generous and reassuring offer! Common in professional and personal contexts! 🤝"
        },

        {
          type: "mc",
          q: "Yemi had ___ the intern ___ so firmly that he nearly cried.",
          options: ["told / off", "said / off", "told / out"],
          a: "told / off",
          tip: "Told someone off = scolded. TELL + person + OFF (not out).",
          successTip: "Tell off (reprimand) vs tell out (not a real phrase!) vs call out (publicly criticise). 'She told him off privately.' 'She called him out publicly.' Different levels! 📣"
        },

        {
          type: "order",
          q: "Traduce: 'Ella dijo lo que pensaba, luego les dijo a todos que el problema estaba resuelto.'",
          words: ["She", "said", "her", "piece,", "then", "told", "everyone", "that", "the", "problem", "was", "resolved."],
          a: "She said her piece, then told everyone that the problem was resolved.",
          extra: ["told", "said", "spoke"]
        },

        {
          type: "mc",
          q: "After the meeting, Mia felt better. She had finally ___ her piece — and been heard.",
          options: ["said", "told", "spoken"],
          a: "said",
          tip: "Say your piece = express yourself (past form: said her piece)."
        },

        {
          type: "input",
          q: "Yemi's management style: 'I always ___ people ___ in private before any public meeting. And I always let everyone ___ their piece.' (tell/off — say)",
          a: "tell / off / say",
          alts: ["Tell / off / say"],
          successTip: "Tell off (reprimand privately) + say your piece (express publicly) — Yemi's two-step approach to conflict. Brilliant management AND brilliant grammar! 🏆"
        }
      ]
    },

    // ===================================================================
    // SET 9: MIXED COLLOCATIONS IN NARRATIVE (B2+)
    // ===================================================================
    {
      id: "m10-u9-set9",
      title: "Set 9: Awards Night",
      context: `*Toronto — National Radio Awards ceremony*

The show had been nominated for Best New Programme.
All four — Mia, Daniel, Yemi, and Priya — were sitting at the same table.
"Just say the word and I'll tell a story that'll win us the award," Daniel whispered.
Yemi said nothing. Mia said yes to more champagne. Priya took out her notepad.

*Toronto — Ceremonia de los Premios Nacionales de Radio*

El programa había sido nominado al Mejor Programa Nuevo.
Los cuatro — Mia, Daniel, Yemi y Priya — estaban sentados en la misma mesa.
"Solo di la señal y cuento una historia que nos ganará el premio," susurró Daniel.
Yemi no dijo nada. Mia dijo que sí a más champán. Priya sacó su libreta.`,

      questions: [
        {
          type: "mc",
          q: "Daniel whispered: 'Just ___ the word and I'll ___ a story that'll win us the award.'",
          options: ["say / tell", "tell / say", "say / say"],
          a: "say / tell",
          tip: "Say the word (fixed SAY) + tell a story (fixed TELL). Classic contrast!",
          successTip: "Say the word + tell a story — these two collocations in one sentence. This is exactly how native speakers talk! Pattern recognition = fluency! 🎯"
        },

        {
          type: "mc",
          q: "Yemi ___ nothing — her expression said everything that needed to be said.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Say nothing = no decir nada. Always SAY."
        },

        {
          type: "mc",
          q: "Mia ___ yes to champagne and ___ Priya that she was incredibly nervous.",
          options: ["said / told", "told / said", "said / said"],
          a: "said / told",
          tip: "Said yes (fixed SAY) + told Priya (TELL + person object).",
          successTip: "Said yes + told Priya — two verbs, two structures, one natural sentence. This is what command of say vs tell looks like! 🥂✅"
        },

        {
          type: "mc",
          q: "Priya had been ___ tales to no one — she kept every secret she had ever been told.",
          options: ["telling", "saying", "speaking"],
          a: "telling",
          tip: "Telling tales = cotilleando. TELL — with the negative 'had been telling tales to no one'.",
          successTip: "'She had been telling tales to no one' = no había cotilleado nada. Great negative use of a TELL collocation. Shows you understand the idiom deeply! 🤐"
        },

        {
          type: "mc",
          q: "When the host announced the winner, Mia couldn't ___ anything for a moment.",
          options: ["say", "tell", "speak"],
          a: "say",
          tip: "Say anything = decir nada/algo (negative context). Always SAY."
        },

        {
          type: "mc",
          q: "On stage, Mia ___ the truth: 'I almost ___ no when they offered me this show.'",
          options: ["told / said", "said / told", "told / told"],
          a: "told / said",
          tip: "Told the truth (TELL collocation) + said no (SAY collocation).",
          successTip: "Told the truth + said no — in ONE direct speech sentence! This is peak say/tell control. One sentence, two verbs, both correct! 🏆🎙️"
        },

        {
          type: "mc",
          q: "She ___ the audience that every single person at her table deserved the award too.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person (the audience) + that clause = TELL."
        },

        {
          type: "mc",
          q: "Daniel leaned over to Yemi: 'I ___ you so — I knew we'd win.'",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told you so = te lo dije. Fixed expression — always TOLD (past of tell).",
          successTip: "'I told you so!' = ¡Te lo dije! One of the most satisfying expressions in English! Always TOLD — never 'I said you so'! ❌ Past form of tell! 😄"
        },

        {
          type: "order",
          q: "Traduce: 'Ella dijo la verdad en el escenario: casi dijo que no, pero alegra de haber dicho que sí.'",
          words: ["She", "told", "the", "truth", "on", "stage:", "she", "almost", "said", "no,", "but", "was", "glad", "she", "had", "said", "yes."],
          a: "She told the truth on stage: she almost said no, but was glad she had said yes.",
          extra: ["told", "said", "spoken"]
        },

        {
          type: "input",
          q: "Priya's headline: 'She ___ the truth, ___ yes to her dream, and ___ the world a story worth telling.' (tell / say / tell)",
          a: "told / said / told",
          alts: ["Told / said / told"],
          successTip: "Told the truth + said yes + told the world a story — three collocations, one headline, zero mistakes. That's say/tell mastery! 🌟🎙️"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL PICTURE — SAY/TELL MASTERY (B2+)
    // ===================================================================
    {
      id: "m10-u9-set10",
      title: "Set 10: Everything Said and Told",
      context: `*One year later — Mia's show is the most listened-to programme in Canada*

Mia was being interviewed about her journey.
She had said yes to the right things, no to the wrong ones.
She had told stories, told the truth, and occasionally told white lies
to guests who asked if they had been good. They almost always had.

*Un año después — El programa de Mia es el más escuchado de Canadá*

Mia era entrevistada sobre su trayectoria.
Había dicho que sí a las cosas correctas, que no a las equivocadas.
Había contado historias, dicho la verdad, y de vez en cuando había dicho mentiras piadosas
a invitados que preguntaban si habían estado bien. Casi siempre sí habían estado.`,

      questions: [
        {
          type: "mc",
          q: "Mia had ___ yes to opportunities that scared her — and ___ no to ones that didn't feel right.",
          options: ["said / said", "told / told", "said / told"],
          a: "said / said",
          tip: "Say yes / say no = both fixed SAY collocations."
        },

        {
          type: "mc",
          q: "She had ___ stories that made people laugh, cry, and think differently.",
          options: ["told", "said", "spoken"],
          a: "told",
          tip: "Tell stories = fixed TELL collocation. Past Perfect: had told."
        },

        {
          type: "mc",
          q: "She had always ___ the truth — even when it was uncomfortable.",
          options: ["told", "said", "spoken"],
          a: "told",
          tip: "Tell the truth = fixed TELL collocation.",
          successTip: "Told the truth — the most important TELL collocation! 'She always told the truth.' vs 'She always said the truth.' ❌ Truth = TELL. Always and forever! ✅"
        },

        {
          type: "mc",
          q: "When guests asked 'Was I good?', she ___ a small white lie: 'You were wonderful.'",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Tell a white lie = fixed TELL collocation."
        },

        {
          type: "mc",
          q: "The interviewer asked her: 'Did anyone ever ___ you off during your first year?'",
          options: ["tell", "say", "speak"],
          a: "tell",
          tip: "Tell someone off = reprender a alguien. TELL + person + off."
        },

        {
          type: "mc",
          q: "Mia laughed: 'Yemi ___ me off at least once a week. And she was right every time.'",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told me off = Past Simple of tell someone off.",
          successTip: "'Told me off every week' = me regañaba cada semana. Tell off = always past TOLD in narrative. 'My teacher told me off for talking.' Very relatable! 😅"
        },

        {
          type: "mc",
          q: "The interviewer ___ that Mia's show had changed Canadian radio.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no person directly after said) = SAY."
        },

        {
          type: "mc",
          q: "Mia ___ the interviewer something she had never ___ publicly before.",
          options: ["told / said", "said / told", "told / told"],
          a: "told / said",
          tip: "Told + person (the interviewer) = TELL. Said publicly (no person) = SAY.",
          successTip: "Told the interviewer (person present) + said publicly (no person, just an adverb) — both in one sentence! You can spot these instantly now! 🔍✅"
        },

        {
          type: "order",
          q: "Traduce: 'Ella le contó la verdad a todos y dijo algo que nadie esperaba oír.'",
          words: ["She", "told", "everyone", "the", "truth", "and", "said", "something", "nobody", "expected", "to", "hear."],
          a: "She told everyone the truth and said something nobody expected to hear.",
          extra: ["said", "told", "anything"]
        },

        {
          type: "input",
          q: "Final quote: 'The best radio,' Mia ___ , 'is when someone ___ their piece, ___ the truth, and ___ something that stays with you.' (said / says / tells / says)",
          a: "said / says / tells / says",
          alts: ["Said / says / tells / says"],
          successTip: "🏆 UNIT 9 COMPLETE! Say ✅ Tell ✅ All collocations ✅ Reported speech ✅ Say your piece, tell the truth, say something — all mastered! You'll never mix these up again! 🎙️🌟"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u9-reading",
    title: "Reading: Said, Told & Everything In Between",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Mia's First Broadcast**

Just as the countdown reached zero, Mia couldn't say anything. She sat frozen for two seconds. Then she said hello to Toronto and told the audience something she had never admitted publicly: she had almost said no to the show.`
      },
      {
        type: "mc",
        q: "What happened when the countdown reached zero?",
        options: ["Mia couldn't say anything", "Mia said goodbye", "Mia told a story"],
        a: "Mia couldn't say anything",
        tip: "'Mia couldn't say anything.'"
      },
      {
        type: "mc",
        q: "What had Mia almost done before accepting the show?",
        options: ["Said no to it", "Told the truth about it", "Said sorry about it"],
        a: "Said no to it",
        tip: "'...she had almost said no to the show.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Daniel's Interview**

During the live interview, Mia asked Daniel whether he always told the truth. He said he did — then immediately told a story that nobody believed. Mia told the audience she couldn't tell the difference. The audience loved it.`
      },
      {
        type: "mc",
        q: "What did Daniel do after saying he told the truth?",
        options: ["Told a story nobody believed", "Said nothing", "Told a white lie"],
        a: "Told a story nobody believed",
        tip: "'...he told a story that nobody believed.'"
      },
      {
        type: "mc",
        q: "What did Mia say she couldn't do?",
        options: ["Tell the difference", "Say sorry", "Tell tales"],
        a: "Tell the difference",
        tip: "'Mia told the audience she couldn't tell the difference.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**The Leak Crisis**

Someone had been telling tales about the show's future plans. Yemi told the intern off privately, then said nothing more about it in public. She told the director that new security protocols were needed. He said the word — and they were implemented within a week.`
      },
      {
        type: "mc",
        q: "What had someone been doing about the show's plans?",
        options: ["Telling tales", "Saying lies", "Telling stories"],
        a: "Telling tales",
        tip: "'Someone had been telling tales about the show's future plans.'"
      },
      {
        type: "mc",
        q: "How did the director respond to Yemi's request?",
        options: ["He said the word", "He said no", "He told her off"],
        a: "He said the word",
        tip: "'He said the word — and they were implemented within a week.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**The Awards Speech**

On stage, Mia told the truth: she had almost said no to the job. She said sorry to Yemi for every chaotic moment she had caused. Then she said something that made everyone smile: "I told you so, Daniel. I told you we'd win."`
      },
      {
        type: "mc",
        q: "Who did Mia say sorry to on stage?",
        options: ["Yemi", "Daniel", "The director"],
        a: "Yemi",
        tip: "'She said sorry to Yemi for every chaotic moment.'"
      },
      {
        type: "mc",
        q: "What did Mia say to Daniel?",
        options: ["I told you so", "Say the word", "Tell the truth"],
        a: "I told you so",
        tip: "'I told you so, Daniel. I told you we'd win.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**One Year On**

A year later, Priya wrote that Mia had said yes to her dream and told stories that changed people's lives. Yemi said nothing — she let the ratings speak for themselves. Daniel told the truth for once: "She made me a better storyteller."` 
      },
      {
        type: "mc",
        q: "What did Priya write that Mia had done?",
        options: ["Said yes to her dream and told stories", "Told lies and said sorry", "Said nothing and told tales"],
        a: "Said yes to her dream and told stories",
        tip: "'...said yes to her dream and told stories that changed people's lives.'"
      },
      {
        type: "mc",
        q: "What did Daniel admit for once?",
        options: ["That Mia made him a better storyteller", "That he had been telling lies", "That he had said yes to the wrong show"],
        a: "That Mia made him a better storyteller",
        tip: "'Daniel told the truth for once: She made me a better storyteller.'",
        successTip: "Every say/tell collocation from the unit appeared in these five passages! Said yes, told stories, told tales, told the truth, said sorry, said nothing, say the word, told you so. Masterclass! 🎙️🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u9-review",
    title: "Unit 9 Review: Say & Tell",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: She ___ yes to the job and never looked back.",
        options: ["said", "told", "spoke"],
        a: "said",
        tip: "Say yes = fixed SAY collocation."
      },
      {
        type: "mc",
        q: "Review: He always ___ the truth, even when it was uncomfortable.",
        options: ["told", "said", "spoke"],
        a: "told",
        tip: "Tell the truth = fixed TELL collocation."
      },
      {
        type: "mc",
        q: "Review: She ___ me she was nervous before the show.",
        options: ["told", "said", "spoke"],
        a: "told",
        tip: "Told + person (me) + that clause = TELL."
      },
      {
        type: "mc",
        q: "Review: Nobody could ___ the difference between his real stories and invented ones.",
        options: ["tell", "say", "know"],
        a: "tell",
        tip: "Tell the difference = distinguir. Fixed TELL collocation."
      },
      {
        type: "mc",
        q: "Review: She ___ nothing when he apologised. Silence was her answer.",
        options: ["said", "told", "spoke"],
        a: "said",
        tip: "Say nothing = guardar silencio. Always SAY."
      },
      {
        type: "mc",
        q: "Review: Yemi ___ the intern ___ for leaking information.",
        options: ["told / off", "said / off", "told / out"],
        a: "told / off",
        tip: "Tell someone off = reprender. TELL + person + OFF."
      },
      {
        type: "mc",
        q: "Review: He ___ a story that kept the entire audience completely silent.",
        options: ["told", "said", "spoke"],
        a: "told",
        tip: "Tell a story = contar una historia. Fixed TELL collocation."
      },
      {
        type: "mc",
        q: "Review: If you disagree, ___ so. Don't stay silent.",
        options: ["say", "tell", "speak"],
        a: "say",
        tip: "Say so = decirlo / afirmarlo. Fixed SAY expression."
      },
      {
        type: "mc",
        q: "Review: She ___ her piece at the meeting and felt much better afterwards.",
        options: ["said", "told", "spoke"],
        a: "said",
        tip: "Say your piece = expresarse / decir lo que piensas."
      },
      {
        type: "mc",
        q: "Review: He ___ me a white lie — he said my presentation had been brilliant.",
        options: ["told", "said", "spoke"],
        a: "told",
        tip: "Tell a white lie = fixed TELL collocation.",
        successTip: "🎉 UNIT 9 MASTERED! Say vs Tell ✅ All collocations ✅ Reported speech ✅ Idiomatic expressions ✅ You'll never mix these up again! Ready for Unit 10! 🚀🎙️"
      }
    ]
  }
};
